import { encodeWav } from '@/util/wav';

/**
 * Microphone recording.
 *
 * Capture goes through an AudioWorklet rather than `MediaRecorder`. That choice
 * is deliberate and costs some code: `MediaRecorder` gives back a lossily
 * encoded blob (Opus in most browsers) with no sample-accurate relationship to
 * the audio clock, which is unacceptable for multitrack work where a take has
 * to line up with everything already recorded. The worklet hands us raw
 * Float32 frames from the audio thread, which we write as WAV — lossless, and
 * positioned by the same clock the scheduler uses.
 *
 * Nothing captured here leaves the machine. The audio goes to an AudioBuffer,
 * to a WAV blob, and into local storage. There is no network path.
 */

export class RecordingError extends Error {
  constructor(
    readonly reason: 'denied' | 'unavailable' | 'noDevice' | 'unsupported' | 'failed',
    message: string,
  ) {
    super(message);
    this.name = 'RecordingError';
  }
}

export interface InputDevice {
  deviceId: string;
  label: string;
}

export interface RecordingTake {
  buffer: AudioBuffer;
  wav: Blob;
  durationSec: number;
  /** Context time at which the first captured sample was taken. */
  startedAtContextTime: number;
}

const PROCESSOR_NAME = 'stage-zero-capture';

/**
 * The worklet processor, as source. It is inlined and loaded from a blob URL so
 * that recording works regardless of how the app is bundled or served, and so
 * there is no second network request between arming and rolling.
 */
const PROCESSOR_SOURCE = `
class CaptureProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this.recording = false;
    this.port.onmessage = (event) => {
      if (event.data === 'start') this.recording = true;
      else if (event.data === 'stop') this.recording = false;
    };
  }
  process(inputs) {
    const input = inputs[0];
    if (this.recording && input && input.length > 0 && input[0] && input[0].length > 0) {
      const channels = [];
      for (let c = 0; c < input.length; c++) channels.push(new Float32Array(input[c]));
      this.port.postMessage(channels, channels.map((c) => c.buffer));
    }
    return true;
  }
}
registerProcessor('${PROCESSOR_NAME}', CaptureProcessor);
`;

export class Recorder {
  private stream: MediaStream | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private node: AudioWorkletNode | null = null;
  private fallbackNode: ScriptProcessorNode | null = null;
  private monitorTarget: AudioNode | null = null;
  private chunks: Float32Array[][] = [];
  private frameCount = 0;
  private channelCount = 1;
  private startedAtContextTime = 0;
  private moduleLoaded = false;
  private levelPeak = 0;

  constructor(private readonly ctx: AudioContext) {}

  /**
   * Lists available inputs. Labels are empty until permission has been granted
   * once, which is a privacy feature of the platform rather than a bug — the UI
   * says so instead of showing a list of "Microphone 1, Microphone 2".
   */
  static async listInputs(): Promise<InputDevice[]> {
    const media = (globalThis as { navigator?: Navigator }).navigator?.mediaDevices;
    if (!media?.enumerateDevices) return [];
    try {
      const devices = await media.enumerateDevices();
      return devices
        .filter((d) => d.kind === 'audioinput')
        .map((d, index) => ({ deviceId: d.deviceId, label: d.label || `Input ${index + 1}` }));
    } catch {
      return [];
    }
  }

  static get isSupported(): boolean {
    const nav = (globalThis as { navigator?: Navigator }).navigator;
    return Boolean(nav?.mediaDevices?.getUserMedia);
  }

  /**
   * Requests microphone access and opens the input. Called only when the user
   * activates recording — never on load — so the permission prompt always has
   * an obvious cause.
   */
  async open(deviceId: string | null): Promise<void> {
    const media = (globalThis as { navigator?: Navigator }).navigator?.mediaDevices;
    if (!media?.getUserMedia) {
      throw new RecordingError(
        'unsupported',
        'This browser cannot capture audio input. Recording needs a secure (https or localhost) page and a browser with getUserMedia.',
      );
    }

    await this.close();

    try {
      this.stream = await media.getUserMedia({
        audio: {
          deviceId: deviceId ? { exact: deviceId } : undefined,
          // Voice-call processing destroys music. All three are disabled.
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
          channelCount: 2,
        },
        video: false,
      });
    } catch (error) {
      throw toRecordingError(error);
    }

    this.source = this.ctx.createMediaStreamSource(this.stream);
    this.channelCount = Math.max(1, this.source.channelCount);

    await this.attachCaptureNode();
  }

  private async attachCaptureNode(): Promise<void> {
    if (!this.source) return;

    if (this.ctx.audioWorklet) {
      try {
        if (!this.moduleLoaded) {
          const url = URL.createObjectURL(new Blob([PROCESSOR_SOURCE], { type: 'text/javascript' }));
          await this.ctx.audioWorklet.addModule(url);
          URL.revokeObjectURL(url);
          this.moduleLoaded = true;
        }
        const node = new AudioWorkletNode(this.ctx, PROCESSOR_NAME, {
          numberOfInputs: 1,
          numberOfOutputs: 1,
          outputChannelCount: [this.channelCount],
        });
        node.port.onmessage = (event: MessageEvent<Float32Array[]>) => this.accept(event.data);
        this.source.connect(node);
        this.node = node;
        return;
      } catch {
        this.node = null;
      }
    }

    // ScriptProcessorNode is deprecated and runs on the main thread, but it is
    // the only capture path left if AudioWorklet is unavailable. Recording at
    // slightly higher latency beats not recording at all.
    const processor = this.ctx.createScriptProcessor(4096, this.channelCount, this.channelCount);
    processor.onaudioprocess = (event) => {
      if (this.chunks.length === 0 && this.frameCount === 0 && !this.capturing) return;
      const channels: Float32Array[] = [];
      for (let c = 0; c < event.inputBuffer.numberOfChannels; c++) {
        channels.push(new Float32Array(event.inputBuffer.getChannelData(c)));
      }
      this.accept(channels);
    };
    this.source.connect(processor);
    processor.connect(this.ctx.destination);
    this.fallbackNode = processor;
  }

  private capturing = false;

  private accept(channels: Float32Array[]): void {
    if (!this.capturing || channels.length === 0) return;
    this.chunks.push(channels);
    this.frameCount += channels[0].length;
    for (let i = 0; i < channels[0].length; i += 16) {
      const v = Math.abs(channels[0][i]);
      if (v > this.levelPeak) this.levelPeak = v;
    }
  }

  /** Routes the live input to a track so the performer can hear themselves. */
  setMonitor(target: AudioNode | null): void {
    if (this.monitorTarget && this.source) {
      try {
        this.source.disconnect(this.monitorTarget);
      } catch {
        /* not connected */
      }
    }
    this.monitorTarget = target;
    if (target && this.source) this.source.connect(target);
  }

  get isOpen(): boolean {
    return this.source !== null;
  }

  /** Peak input level since the last read, for the input meter. */
  readLevel(): number {
    const level = this.levelPeak;
    this.levelPeak = 0;
    return level;
  }

  start(): void {
    this.chunks = [];
    this.frameCount = 0;
    this.capturing = true;
    this.startedAtContextTime = this.ctx.currentTime;
    this.node?.port.postMessage('start');
  }

  /** Ends capture and assembles the take. Returns null if nothing was captured. */
  stop(): RecordingTake | null {
    this.capturing = false;
    this.node?.port.postMessage('stop');
    if (this.frameCount === 0) return null;

    const channelCount = this.chunks[0]?.length ?? 1;
    // Allocated over explicit ArrayBuffers so they satisfy `copyToChannel`,
    // which will not accept a possibly-shared backing store.
    const channels: Float32Array<ArrayBuffer>[] = [];
    for (let c = 0; c < channelCount; c++) channels.push(new Float32Array(new ArrayBuffer(this.frameCount * 4)));

    let offset = 0;
    for (const chunk of this.chunks) {
      const length = chunk[0].length;
      for (let c = 0; c < channelCount; c++) channels[c].set(chunk[Math.min(c, chunk.length - 1)], offset);
      offset += length;
    }
    this.chunks = [];

    const buffer = this.ctx.createBuffer(channelCount, this.frameCount, this.ctx.sampleRate);
    for (let c = 0; c < channelCount; c++) buffer.copyToChannel(channels[c], c);

    const wav = new Blob([encodeWav(channels, this.ctx.sampleRate, { bitDepth: 24 }) as BlobPart], {
      type: 'audio/wav',
    });

    return {
      buffer,
      wav,
      durationSec: this.frameCount / this.ctx.sampleRate,
      startedAtContextTime: this.startedAtContextTime,
    };
  }

  /** Throws away anything captured so far. */
  cancel(): void {
    this.capturing = false;
    this.node?.port.postMessage('stop');
    this.chunks = [];
    this.frameCount = 0;
  }

  async close(): Promise<void> {
    this.cancel();
    this.setMonitor(null);
    this.node?.port.close();
    this.node?.disconnect();
    this.node = null;
    if (this.fallbackNode) {
      this.fallbackNode.onaudioprocess = null;
      this.fallbackNode.disconnect();
      this.fallbackNode = null;
    }
    this.source?.disconnect();
    this.source = null;
    // Releasing the tracks turns off the browser's recording indicator, which
    // must never stay lit after the user has stopped recording.
    for (const track of this.stream?.getTracks() ?? []) track.stop();
    this.stream = null;
  }
}

function toRecordingError(error: unknown): RecordingError {
  const name = error instanceof DOMException ? error.name : '';
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return new RecordingError(
        'denied',
        'Microphone access was denied. Stage Zero needs it only while you are recording, and the audio never leaves your computer. ' +
          'Allow the microphone in your browser’s site settings to record.',
      );
    case 'NotFoundError':
    case 'OverconstrainedError':
      return new RecordingError(
        'noDevice',
        'No matching audio input was found. Connect an input device, or choose a different one in the track’s input menu.',
      );
    case 'NotReadableError':
      return new RecordingError(
        'unavailable',
        'The audio input is in use by another application and could not be opened. Close the other application and try again.',
      );
    default:
      return new RecordingError(
        'failed',
        `Recording could not start: ${error instanceof Error ? error.message : String(error)}`,
      );
  }
}
